// Logic Model (API Request/Response)
export interface MerchantRewardHistoryApiRequest {
    filter: MerchantRewardHistoryFilter;
    pagination: {
        index: number;
        size: number;
    };
}

export interface MerchantRewardHistoryFilter {
    user_name?: string | null;
    user_email?: string | null;
    reward_name?: string | null; // UI uses reward_name, API uses coupon_name
    redemption_status?: 'redeemed' | 'used' | 'viewed' | null;
    [key: string]: string | null | undefined;
}

// Raw API Response - List: POST /v1/merchant/local-merchant-reward-history
// Response: { success, http_request_status, message, user_message, data: { data: Item[], pagination } }
export interface MerchantRewardHistoryItemRawApiResponse {
    id: string;
    user_id: string;
    user_name: string;
    user_email: string;
    user_avatar?: string;
    coupon_id: string;
    coupon_name: string;
    points_used: number;
    exchange_date: string;
    redemption_status: 'redeemed' | 'used' | 'viewed';
    redeemed_date?: string | null;
    used_date?: string | null;
}

export interface MerchantRewardHistoryRawApiResponse {
    data: MerchantRewardHistoryItemRawApiResponse[];
    pagination: {
        count: number;  // items on current page
        index: number;
        size: number;
        total: number;   // total records
    };
}

// Raw API Response - Statistic: GET /v1/merchant/local-merchant-coupon/statistic
export interface MerchantCouponStatisticRawApiResponse {
    total_active_coupons: number;
    total_coupons: number;
    total_viewed_coupons: number;
    coupons_redeemed: number;
    usage_count: number;
    low_or_out_of_stock: number;
}

// API Model (internal, with all fields)
export interface MerchantRewardHistoryItemApi {
    id: string;
    user_id: string;
    user_name: string;
    user_email: string;
    user_avatar?: string;
    coupon_id: string;
    coupon_name: string;
    coupon_code: string; // Default empty if not in API
    points_used: number;
    exchange_date: string;
    redemption_status: 'redeemed' | 'used' | 'viewed';
    resend_count: number; // Default 0 if not in API
    redeemed_date?: string;
    used_date?: string;
    resend_date?: string; // Not in API response
}

export interface MerchantRewardHistoryStatisticApi {
    total_sent_rewards: number;
    total_redeemed_rewards: number;
}

export interface MerchantRewardHistoryPaginationResponse {
    count: number;
    index: number;
    size: number;
}

export interface MerchantRewardHistoryListResponse {
    data: MerchantRewardHistoryItemApi[];
    pagination: MerchantRewardHistoryPaginationResponse;
}

export interface ResendRewardResponse {
    resend_count: number;
    resend_date: string;
}

// UI Model (State Management)
export interface MerchantRewardHistoryItemUI {
    id: string;
    user: {
        name: string;
        email: string;
        avatar?: string;
    };
    reward: {
        name: string;
        image?: string;
    };
    dateRedeemed: string;
    pointsUsed: number;
    status: 'Redeemed' | 'Used' | 'Viewed' | 'Failed';
    actions: {
        canResend: boolean;
        lastResent?: string;
    };
}

export interface MerchantRewardHistoryPageState {
    items: MerchantRewardHistoryItemUI[];
    statistic: {
        totalRedeemedRewards: number;
        totalSentRewards: number;
    };
    filters: MerchantRewardHistoryFilter;
    pagination: {
        index: number;
        size: number;
        count: number;
    };
    loading: {
        onGetList: boolean;
        onGetStatistic: boolean;
        onResend: boolean;
    };
    error: string | null;
    selectedItem: MerchantRewardHistoryItemUI | null;
}

// Data Mapper
const formatDateToClientTimezone = (dateString: string): string => {
    if (!dateString) return '';
    
    try {
        const date = new Date(dateString);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        
        // Format time in 12-hour format with AM/PM
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // 0 should be 12
        const formattedHours = String(hours).padStart(2, '0');
        
        return `${month}/${day}/${year} ${formattedHours}:${minutes} ${ampm}`;
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString; // Return original if formatting fails
    }
};

const mapAPIStatusToUI = (apiStatus: 'redeemed' | 'used' | 'viewed'): 'Redeemed' | 'Used' | 'Viewed' | 'Failed' => {
    switch (apiStatus) {
        case 'redeemed':
            return 'Redeemed';
        case 'used':
            return 'Used';
        case 'viewed':
            return 'Viewed';
        default:
            return 'Failed';
    }
};

// Mapper from raw API response to internal API model
export const mapRawApiResponseToApiResponse = (
    rawData: MerchantRewardHistoryItemRawApiResponse
): MerchantRewardHistoryItemApi => {
    return {
        id: rawData.id,
        user_id: rawData.user_id,
        user_name: rawData.user_name,
        user_email: rawData.user_email,
        user_avatar: rawData.user_avatar,
        coupon_id: rawData.coupon_id,
        coupon_name: rawData.coupon_name,
        coupon_code: '', // Not in API response, default empty
        points_used: rawData.points_used,
        exchange_date: rawData.exchange_date,
        redemption_status: rawData.redemption_status,
        resend_count: 0, // Not in API response, default 0
        redeemed_date: rawData.redeemed_date ?? undefined,
        used_date: rawData.used_date ?? undefined,
        resend_date: undefined, // Not in API response
    };
};

export const mapApiResponseToUI = (apiData: MerchantRewardHistoryItemApi): MerchantRewardHistoryItemUI => {
    return {
        id: apiData.id,
        user: {
            name: apiData.user_name || '',
            email: apiData.user_email || '',
            avatar: apiData.user_avatar
        },
        reward: {
            name: apiData.coupon_name || '',
            image: undefined // API doesn't provide image
        },
        dateRedeemed: formatDateToClientTimezone(apiData.redeemed_date || ''),
        pointsUsed: apiData.points_used || 0,
        status: mapAPIStatusToUI(apiData.redemption_status),
        actions: {
            canResend: apiData.redemption_status === 'viewed',
            lastResent: apiData.resend_date ? formatDateToClientTimezone(apiData.resend_date) : undefined
        }
    };
};

export const initialMerchantRewardHistoryPageState: MerchantRewardHistoryPageState = {
    items: [],
    statistic: {
        totalRedeemedRewards: 0,
        totalSentRewards: 0,
    },
    filters: {},
    pagination: {
        index: 1,
        size: 20,
        count: 0,
    },
    loading: {
        onGetList: false,
        onGetStatistic: false,
        onResend: false,
    },
    error: null,
    selectedItem: null,
};

